import * as React from 'react';

import { flexRender, type Table as TanstackTable } from '@tanstack/react-table';

import { DataTablePageBased } from '@/components/data-table/data-table-page-based';
import { TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { getCommonPinningStyles } from '@/lib/data-table';
import { cn } from '@/lib/utils';

import { DataTableResizeHandle } from './data-table-resize-handle';
import { DataTableSortingArrows } from './data-table-sorting-arrows';
import { GroupedTableBody } from './grouped-table-body';
import { useFilledColumnWidths } from './hooks/use-filled-column-widths';
import { useScrollShadows } from './hooks/use-scroll-shadows';
import { TableBodyContent, type TableBodyContentProps } from './table-body-content';
import type { TableGroup, TableRowInteractionProps, TableVariants } from './types';
import { getColumnWidthStyle, isSelectColumn } from './utils/column-width-style';
import { VirtualizedTableBody } from './virtualized-table-body';

export type { TableVariants } from './types';

const DEFAULT_ESTIMATED_ROW_HEIGHT_PX = 56;

interface DataTableProps<TData>
  extends React.ComponentProps<'div'>,
    TableRowInteractionProps<TData>,
    Pick<
      TableBodyContentProps<TData>,
      'shouldAnimateRowEnter' | 'registerRowRef' | 'renderFullWidthRow'
    > {
  table: TanstackTable<TData>;
  actionBar?: React.ReactNode;
  lastRowChildren?: React.ReactNode;
  /**
   * If true, table header becomes sticky and body scrolls independently.
   * Container must have defined height for this to work.
   */
  stickyHeader?: boolean;
  /** Optional ref to the scrollable container for infinite scroll integration. */
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
  /** Optional grouped data for rendering category headers between rows. */
  groupedData?: TableGroup<TData>[];
  /** Function to render group header content. */
  renderGroupHeader?: (category: string) => React.ReactNode;
  variant?: TableVariants;
  /** Unique table ID for enabling column resizing with localStorage persistence. */
  tableId?: string;
  /** Callback when column resize ends - used to persist column widths. */
  onResizeEnd?: () => void;
  /**
   * Number of trailing columns that should share remaining space equally.
   * Default is 1 (only the last column fills remaining space).
   */
  fillLastColumns?: number;
  /**
   * Opt-in row virtualization (renders only visible rows). Takes effect ONLY on the
   * `stickyHeader` + non-grouped path; ignored otherwise. Use for very large tables
   * (thousands of rows). Default false — all existing consumers render unchanged.
   */
  enableVirtualization?: boolean;
  /** Estimated row height (px) for the virtualizer; rows are then measured. Default 56. */
  estimateRowHeight?: number;
}

export function DataTable<TData>({
  table,
  actionBar,
  onRowClick,
  getRowHref,
  children,
  lastRowChildren,
  stickyHeader = false,
  scrollContainerRef,
  groupedData,
  renderGroupHeader,
  getRowClassName,
  shouldAnimateRowEnter,
  className,
  variant,
  tableId,
  onResizeEnd,
  fillLastColumns = 1,
  registerRowRef,
  renderFullWidthRow,
  enableVirtualization = false,
  estimateRowHeight = DEFAULT_ESTIMATED_ROW_HEIGHT_PX,
  ...props
}: DataTableProps<TData>) {
  const internalScrollRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = scrollContainerRef ?? internalScrollRef;

  const { isScrolledHorizontally, isScrolledVertically } = useScrollShadows(scrollRef);
  const { filledColumnWidths, tableWidth } = useFilledColumnWidths({
    table,
    scrollContainerRef: scrollRef,
    tableId,
    fillLastColumns,
  });

  const isGrouped = !!groupedData && !!renderGroupHeader;
  // Virtualization is opt-in and ONLY supported on the sticky + non-grouped path.
  const isVirtualized = enableVirtualization && stickyHeader && !isGrouped;

  const bodyProps = {
    table,
    isScrolled: isScrolledHorizontally,
    onRowClick,
    getRowHref,
    getRowClassName,
    variant,
    tableId,
    filledColumnWidths,
    lastRowChildren,
  };

  const header = (
    <TableHeaderContent
      table={table}
      isScrolled={isScrolledHorizontally}
      variant={variant}
      tableId={tableId}
      onResizeEnd={onResizeEnd}
      filledColumnWidths={filledColumnWidths}
    />
  );

  const body = isVirtualized ? (
    <VirtualizedTableBody
      {...bodyProps}
      registerRowRef={registerRowRef}
      renderFullWidthRow={renderFullWidthRow}
      scrollElementRef={scrollRef}
      estimateRowHeight={estimateRowHeight}
      tableWidth={tableWidth}
    />
  ) : (
    <TableBody className={stickyHeader ? 'bg-background' : undefined}>
      {isGrouped ? (
        <GroupedTableBody
          {...bodyProps}
          groups={groupedData}
          renderGroupHeader={renderGroupHeader}
        />
      ) : (
        <TableBodyContent
          {...bodyProps}
          shouldAnimateRowEnter={shouldAnimateRowEnter}
          registerRowRef={registerRowRef}
          renderFullWidthRow={renderFullWidthRow}
        />
      )}
    </TableBody>
  );

  const tableElement = (
    <table
      className="w-full caption-bottom text-sm"
      style={tableWidth ? { width: tableWidth, tableLayout: 'fixed' } : undefined}
    >
      <TableHeader
        className={cn(
          stickyHeader && 'sticky top-0 z-10 bg-muted',
          stickyHeader && isScrolledVertically && 'shadow-[0_1px_3px_0_rgb(0,0,0,0.1)]'
        )}
      >
        {header}
      </TableHeader>
      {body}
    </table>
  );

  const footer = <TableFooterContent table={table} actionBar={actionBar} />;

  if (stickyHeader) {
    return (
      <div className={cn('flex min-h-0 w-full flex-col gap-2.5', className)} {...props}>
        {children}
        <div className="flex max-h-full min-h-0 flex-col overflow-hidden rounded-md border">
          <div
            ref={scrollRef}
            className="relative w-full overflow-auto bg-muted [scrollbar-gutter:stable]"
            // `overflowAnchor: none` — browser scroll anchoring fights the virtualizer when
            // row heights/offsets change (approve/unapprove refetch, resize-end remeasure)
            // and randomly jumps the scroll position; the virtualizer owns positioning here.
            style={
              { '--table-header-height': '47px', overflowAnchor: 'none' } as React.CSSProperties
            }
            data-scroll-container
          >
            {tableElement}
          </div>
        </div>
        <div className="flex shrink-0 flex-col gap-2.5">{footer}</div>
      </div>
    );
  }

  return (
    <div className={cn('flex w-full flex-col gap-2.5 overflow-auto', className)} {...props}>
      {children}
      <div className="overflow-hidden rounded-md border">
        <div
          ref={scrollRef}
          className="overflow-auto"
          style={{
            background:
              'linear-gradient(to bottom, hsl(var(--muted)) 48px, hsl(var(--background)) 48px)',
          }}
        >
          {tableElement}
        </div>
      </div>
      <div className="flex flex-col gap-2.5">{footer}</div>
    </div>
  );
}

interface TableHeaderContentProps<TData> {
  table: TanstackTable<TData>;
  isScrolled: boolean;
  variant?: TableVariants;
  tableId?: string;
  onResizeEnd?: () => void;
  filledColumnWidths?: Map<string, number>;
}

/** Header rows with sorting and, for resizable tables, a resize handle per column. */
function TableHeaderContent<TData>({
  table,
  isScrolled,
  variant,
  tableId,
  onResizeEnd,
  filledColumnWidths,
}: TableHeaderContentProps<TData>) {
  return (
    <>
      {table.getHeaderGroups().map(headerGroup => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map(header => {
            const isSortable = !!header.column.columnDef.enableSorting;
            const isResizable =
              !!tableId && !filledColumnWidths?.has(header.id) && !isSelectColumn(header.column.id);

            return (
              <TableHead
                key={header.id}
                colSpan={header.colSpan}
                variant={variant}
                className={cn(
                  'group/header relative',
                  header.column.getIsResizing() && 'select-none'
                )}
                style={{
                  ...getCommonPinningStyles({ column: header.column, withBorder: isScrolled }),
                  ...getColumnWidthStyle({
                    column: header.column,
                    size: header.getSize(),
                    tableId,
                    filledColumnWidths,
                  }),
                  cursor: isSortable ? 'pointer' : undefined,
                }}
                onClick={isSortable ? () => header.column.toggleSorting() : undefined}
              >
                <div className="flex h-full items-center">
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                  {isSortable && <DataTableSortingArrows isSorted={header.column.getIsSorted()} />}
                </div>

                {isResizable && <DataTableResizeHandle header={header} onResizeEnd={onResizeEnd} />}
              </TableHead>
            );
          })}
        </TableRow>
      ))}
    </>
  );
}

interface TableFooterContentProps<TData> {
  table: TanstackTable<TData>;
  actionBar?: React.ReactNode;
}

/** Page-based pagination controls plus the bulk action bar shown while rows are selected. */
function TableFooterContent<TData>({ table, actionBar }: TableFooterContentProps<TData>) {
  return (
    <>
      {typeof table.options.pageCount === 'number' && table.options.pageCount > 0 && (
        <DataTablePageBased table={table} />
      )}
      {actionBar && table.getFilteredSelectedRowModel().rows.length > 0 && actionBar}
    </>
  );
}
